// POSPage.tsx
import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

// Canonical types — single source of truth
import type { Product, Customer, PaymentType } from '../types'

// Centralized stores
import { useProductsStore } from '../store/productsStore'
import { useCartStore } from '../store/cartStore'

import { barcodeScanner } from '../lib/barcodeScanner'
import { barcodeGenerator } from '../lib/barcodeGenerator'
import { taxCalculator } from '../lib/taxCalculator'
import { 
  Search, Barcode, Grid, List, Plus, Trash2, User, Lock, 
  DollarSign, Package, ShoppingCart, CreditCard, RotateCcw, 
  Menu, ChevronRight, LogOut, UserCircle, MessageSquare, 
  Power, Maximize, FileText, Tag, ShieldCheck, X, Save,
  ArrowLeft, Percent, MessageCircle, Users, Receipt,
  Clock, Calendar, PackageCheck, AlertCircle
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../store/authStore'
import { apiClient } from '../lib/api'

// Page-specific types (not in shared types — these are API request payload shapes)
interface POSCartItem {
  id: string
  product_id: string
  product_code: string
  product_name: string
  quantity: number
  unit_price: number
  original_price: number
  discount_percent: number
  discount_amount: number
  tax_rate_id?: string
  tax_percent: number
  tax_amount: number
  total_amount: number
  line_number: number
  parent_line_number?: number
  serial_number?: string
  lot_number?: string
  expiry_date?: string
  preparation_instructions?: string
  course_number?: number
  seat_number?: number
  kitchen_status?: string
  round_number: number
  is_confirmed: boolean
  is_canceled: boolean
  is_voided: boolean
  special_instructions?: string
  cost_price?: number
  preparation_status?: string
  modifier_total: number
  added_at: string
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'served' | 'cancelled'
}

interface TaxRate {
  id: string
  name: string
  code: string
  rate: number
  is_active: boolean
  is_default: boolean
}

interface OrderData {
  order_number?: string
  order_date: string
  customer_id?: string
  user_id: string
  cash_register_id?: string
  business_day_id?: string
  table_id?: string
  room_id?: string
  status: string
  order_type: string
  subtotal: number
  discount_amount: number
  tax_amount: number
  total_amount: number
  paid_amount: number
  change_amount: number
  is_parked: boolean
  is_locked: boolean
  is_voided: boolean
  payment_status: string
  notes?: string
  total_tips: number
  service_charge: number
  delivery_fee: number
  customer_count: number
  table_number?: string
  waiter_id?: string
  kitchen_status?: string
  preparation_time?: number
  round_number: number
  transaction_type: string
  invoice_number?: string
  receipt_printed_count: number
  invoice_printed_count: number
  email_sent: boolean
  sms_sent: boolean
  guest_count: number
  special_instructions?: string
  order_source: string
  source_channel?: string
  items: OrderItemData[]
  payments: OrderPaymentData[]
}

interface OrderItemData {
  product_id: string
  product_code: string
  product_name: string
  line_number: number
  parent_line_number?: number
  quantity: number
  unit_price: number
  original_price: number
  discount_percent: number
  discount_amount: number
  tax_rate_id?: string
  tax_percent: number
  tax_amount: number
  total_amount: number
  cost_price?: number
  preparation_instructions?: string
  course_number?: number
  seat_number?: number
  round_number: number
  special_instructions?: string
  kitchen_status?: string
}

interface OrderPaymentData {
  payment_type_id: string
  amount: number
  tendered_amount: number
  change_amount: number
  reference_number?: string
  card_type?: string
  card_last_four?: string
  authorization_code?: string
  terminal_id?: string
}

export default function POSPage() {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()

  // Centralized product store — single source of truth for products/categories
  const { products, fetchProducts, isLoading: productsLoading } = useProductsStore()

  // Centralized cart store — single source of truth for cart state and totals
  const {
    items: cartItems,
    customer: selectedCustomer,
    setCustomer: setSelectedCustomer,
    addItem: cartAddItem,
    updateQuantity: cartUpdateQuantity,
    voidItem: cartVoidItem,
    clearCart,
    subtotal: cartSubtotal,
    totalDiscountAmount: cartDiscount,
    taxAmount: cartTaxAmount,
    total: cartTotal,
  } = useCartStore()

  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'barcode'>('grid')
  const [searchQuery, setSearchQuery] = useState('')
  const [showFlyout, setShowFlyout] = useState(false)
  const [showSearchDialog, setShowSearchDialog] = useState(false)
  const [showQuantityDialog, setShowQuantityDialog] = useState(false)
  const [showNewSaleDialog, setShowNewSaleDialog] = useState(false)
  const [showCustomerDialog, setShowCustomerDialog] = useState(false)
  const [showDiscountDialog, setShowDiscountDialog] = useState(false)
  const [showCommentDialog, setShowCommentDialog] = useState(false)
  const [showPaymentDialog, setShowPaymentDialog] = useState(false)
  const [quantityInput, setQuantityInput] = useState('1')
  const [newSaleInput, setNewSaleInput] = useState('')
  const [customerSearch, setCustomerSearch] = useState('')
  const [discountInput, setDiscountInput] = useState('')
  const [commentInput, setCommentInput] = useState('')
  const [paymentAmount, setPaymentAmount] = useState('')
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentType | null>(null)
  const [rightPanelWidth, setRightPanelWidth] = useState(410)
  const [isResizing, setIsResizing] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [bgLoaded, setBgLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [customers, setCustomers] = useState<Customer[]>([])
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([])
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [currentBusinessDayId, setCurrentBusinessDayId] = useState<string | null>(null)

  const searchInputRef = useRef<HTMLInputElement>(null)
  const quantityInputRef = useRef<HTMLInputElement>(null)
  const newSaleInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const img = new Image()
    img.src = '/auth-bg.png'
    img.onload = () => setBgLoaded(true)
    img.onerror = () => setBgLoaded(true)
  }, [])

  useEffect(() => {
    // Load initial data
    loadInitialData()

    // Load current business day
    loadCurrentBusinessDay()

    // Set up keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent default behavior for function keys
      if (e.key.startsWith('F') && !e.key.includes('Meta')) {
        e.preventDefault()
      }

      switch (e.key) {
        case 'F3':
          setShowSearchDialog(true)
          break
        case 'F4':
          setShowQuantityDialog(true)
          break
        case 'F8':
          setShowNewSaleDialog(true)
          break
        case 'F9':
          handleSaveSale()
          break
        case 'F10':
          handlePayment()
          break
        case 'F12':
          handleCashPayment()
          break
        case 'F2':
          setShowDiscountDialog(true)
          break
        case 'F7':
          handleLockTerminal()
          break
        case 'Escape':
          if (showSearchDialog) setShowSearchDialog(false)
          if (showQuantityDialog) setShowQuantityDialog(false)
          if (showNewSaleDialog) setShowNewSaleDialog(false)
          if (showCustomerDialog) setShowCustomerDialog(false)
          if (showDiscountDialog) setShowDiscountDialog(false)
          if (showCommentDialog) setShowCommentDialog(false)
          if (showPaymentDialog) setShowPaymentDialog(false)
          break
        case 'Enter':
          if (showQuantityDialog) {
            handleQuantityConfirm()
          } else if (showNewSaleDialog && newSaleInputRef.current) {
            newSaleInputRef.current.focus()
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    // Wire hardware barcode scanner — scanned codes trigger product lookup
    barcodeScanner.start()
    const unsubscribeScan = barcodeScanner.onScan((result) => {
      if (result.isValid && result.clean) {
        handleSearchProduct(result.clean)
      }
    })

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      barcodeScanner.stop()
      unsubscribeScan()
    }
  }, [])

  const loadInitialData = async () => {
    try {
      setIsLoading(true)

      // Products are loaded via the centralized productsStore (single source of truth)
      await fetchProducts()

      // Load payment types
      const paymentTypesResponse = await apiClient.get('/api/payment-types?is_active=true')
      const pts: PaymentType[] = paymentTypesResponse.data?.data ?? []
      setPaymentTypes(pts)

      // Set default payment type
      const defaultPaymentType = pts.find((pt) => pt.is_default)
      setSelectedPaymentType(defaultPaymentType ?? null)

      // Load tax rates
      const taxRatesResponse = await apiClient.get('/api/tax-rates?is_active=true')
      setTaxRates(taxRatesResponse.data?.data ?? [])

    } catch (error: any) {
      console.error('Failed to load initial data:', error)
      toast.error('Failed to load initial data')
    } finally {
      setIsLoading(false)
    }
  }

  const loadCurrentBusinessDay = async () => {
    try {
      const response = await apiClient.get('/api/business-days/current')
      if (response.data?.data?.id) {
        setCurrentBusinessDayId(response.data.data.id)
      }
    } catch (error: any) {
      console.error('Failed to load business day:', error)
      // Don't show error toast - might not have business day open yet
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true)
    e.preventDefault()
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return
      const newWidth = window.innerWidth - e.clientX
      if (newWidth >= 310 && newWidth <= 600) {
        setRightPanelWidth(newWidth)
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing])

  const handleNumericKey = (key: string) => {
    if (key === 'ESC') {
      setShowQuantityDialog(false)
      setQuantityInput('1')
    } else if (key === '←') {
      setQuantityInput(quantityInput.slice(0, -1) || '1')
    } else if (key === 'Enter') {
      handleQuantityConfirm()
    } else if (key === '.') {
      if (!quantityInput.includes('.')) {
        setQuantityInput(quantityInput + '.')
      }
    } else if (key === '-') {
      setShowQuantityDialog(false)
    } else {
      setQuantityInput(quantityInput === '1' ? key : quantityInput + key)
    }
  }

  const handleQuantityConfirm = () => {
    const quantity = parseFloat(quantityInput)
    if (quantity > 0 && !isNaN(quantity)) {
      const lastAddedItem = cartItems.find(item => !item.is_canceled && !item.is_voided)
      if (lastAddedItem) {
        const product = products.find(p => p.id === lastAddedItem.product_id)
        if (product?.track_inventory && product.current_stock !== undefined) {
          const otherQty = cartItems
            .filter(i => i.product_id === product.id && !i.is_canceled && !i.is_voided && i.id !== lastAddedItem.id)
            .reduce((sum, i) => sum + i.quantity, 0)
          if (otherQty + quantity > product.current_stock && !product.allow_backorder) {
            toast.error(`Only ${product.current_stock} in stock`)
            return
          }
        }
        cartUpdateQuantity(lastAddedItem.id, quantity)
        toast.success(`Quantity updated to ${quantity}`)
      }
    }
    setShowQuantityDialog(false)
    setQuantityInput('1')
  }

  const handleDialogOverlayClick = (e: React.MouseEvent, dialogType: string) => {
    if (e.target === e.currentTarget) {
      switch (dialogType) {
        case 'quantity':
          setShowQuantityDialog(false)
          setQuantityInput('1')
          break
        case 'search':
          setShowSearchDialog(false)
          break
        case 'newSale':
          setShowNewSaleDialog(false)
          setNewSaleInput('')
          break
        case 'customer':
          setShowCustomerDialog(false)
          break
        case 'discount':
          setShowDiscountDialog(false)
          break
        case 'comment':
          setShowCommentDialog(false)
          break
        case 'payment':
          setShowPaymentDialog(false)
          break
      }
    }
  }

  const handleSearchProduct = async (query: string) => {
    if (!query.trim()) return

    try {
      const response = await apiClient.get(`/api/products/search?q=${encodeURIComponent(query)}`)
      const product = response.data.data?.[0]

      if (product) {
        addProductToCart(product)
      } else {
        toast.error('Product not found')
      }
    } catch (error: any) {
      console.error('Search error:', error)
      toast.error('Failed to search product')
    }
  }

  const addProductToCart = (product: Product) => {
    if (!product.is_active) {
      toast.error(`${product.name} is not available`)
      return
    }

    // Stock guard before delegating to cartStore
    const cartQuantity = cartItems
      .filter(item =>
        item.product_id === product.id &&
        !item.is_canceled &&
        !item.is_voided
      )
      .reduce((sum, item) => sum + item.quantity, 0)

    if (product.track_inventory && product.current_stock !== undefined) {
      if (cartQuantity + 1 > product.current_stock && !product.allow_backorder) {
        toast.error(`Cannot add more. Only ${product.current_stock} in stock.`)
        return
      }
    }

    cartAddItem(product)
    toast.success(`${product.name} added to cart`)
  }

  const removeFromCart = (itemId: string) => {
    cartVoidItem(itemId)
  }

  const updateCartItemQuantity = (itemId: string, change: number) => {
    const item = cartItems.find(i => i.id === itemId)
    if (!item) return

    const newQuantity = item.quantity + change
    if (newQuantity < 1) {
      toast.error('Quantity cannot be less than 1')
      return
    }

    const product = products.find(p => p.id === item.product_id)
    if (product?.track_inventory && product.current_stock !== undefined) {
      const otherCartQty = cartItems
        .filter(i => i.product_id === product.id && !i.is_canceled && !i.is_voided && i.id !== itemId)
        .reduce((sum, i) => sum + i.quantity, 0)
      if (otherCartQty + newQuantity > product.current_stock && !product.allow_backorder) {
        toast.error(`Only ${product.current_stock} in stock`)
        return
      }
    }

    cartUpdateQuantity(itemId, newQuantity)
  }

  // Totals derived from cartStore (single source of truth — no local recalculation)
  const activeCartItems = cartItems.filter(item => !item.is_canceled && !item.is_voided)
  const totals = {
    subtotal: Number(cartSubtotal.toFixed(2)),
    discount: Number(cartDiscount.toFixed(2)),
    tax: Number(cartTaxAmount.toFixed(2)),
    total: Number(cartTotal.toFixed(2)),
    itemCount: activeCartItems.reduce((sum, item) => sum + item.quantity, 0),
  }

  const handleSaveSale = async () => {
    if (activeCartItems.length === 0) {
      toast.error('Cart is empty')
      return
    }

    try {
      const now = new Date().toISOString()

      const orderData: Partial<OrderData> = {
        order_date: now,
        user_id: user?.id || '',
        status: 'open',
        order_type: 'sale',
        subtotal: totals.subtotal,
        discount_amount: totals.discount,
        tax_amount: totals.tax,
        total_amount: totals.total,
        paid_amount: 0,
        change_amount: 0,
        is_parked: false,
        is_locked: false,
        is_voided: false,
        payment_status: 'unpaid',
        customer_count: 1,
        round_number: 1,
        transaction_type: 'sale',
        receipt_printed_count: 0,
        invoice_printed_count: 0,
        email_sent: false,
        sms_sent: false,
        guest_count: 1,
        order_source: 'pos_web',
        items: activeCartItems.map((item, index) => ({
          product_id: item.product_id,
          product_code: item.product?.code ?? '',
          product_name: item.productName,
          line_number: index + 1,
          quantity: item.quantity,
          unit_price: item.unit_price,
          original_price: item.originalPrice,
          discount_percent: item.discount_percent,
          discount_amount: item.discount_amount,
          tax_rate_id: undefined,
          tax_percent: item.tax_rate,
          tax_amount: item.tax_amount,
          total_amount: item.total,
          cost_price: item.product?.cost_price,
          round_number: item.round_number,
          kitchen_status: 'pending',
        }))
      }

      if (selectedCustomer) {
        orderData.customer_id = selectedCustomer.id
      }

      if (currentBusinessDayId) {
        orderData.business_day_id = currentBusinessDayId
      }

      const response = await apiClient.post('/api/orders', orderData)

      if (response.data && response.data.id) {
        toast.success(`Order #${response.data.order_number} saved successfully!`)
      }
    } catch (error: any) {
      console.error('Save sale error:', error)
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save order'
      toast.error(`Save failed: ${errorMessage}`)
    }
  }

  const handlePayment = () => {
    if (activeCartItems.length === 0) {
      toast.error('Cart is empty')
      return
    }
    setPaymentAmount(totals.total.toFixed(2))
    setShowPaymentDialog(true)
  }

  const handleCashPayment = () => {
    const cashPaymentType = paymentTypes.find(pt => pt.code?.toLowerCase() === 'cash')
    if (cashPaymentType) {
      setSelectedPaymentType(cashPaymentType)
      handlePayment()
    }
  }

  const processPayment = async () => {
    const paymentAmountNum = parseFloat(paymentAmount)

    if (isNaN(paymentAmountNum) || paymentAmountNum <= 0) {
      toast.error('Invalid payment amount')
      return
    }

    if (!selectedPaymentType) {
      toast.error('Please select a payment type')
      return
    }

    try {
      setIsLoading(true)
      const now = new Date().toISOString()
      const changeAmount = Math.max(0, paymentAmountNum - totals.total)

      const orderData: any = {
        order_date: now,
        user_id: user?.id || '',
        status: 'completed',
        order_type: 'sale',
        subtotal: totals.subtotal,
        discount_amount: totals.discount,
        tax_amount: totals.tax,
        total_amount: totals.total,
        paid_amount: totals.total,
        change_amount: changeAmount,
        is_parked: false,
        is_locked: false,
        is_voided: false,
        payment_status: 'paid',
        customer_count: 1,
        round_number: 1,
        transaction_type: 'sale',
        receipt_printed_count: 1,
        invoice_printed_count: 0,
        email_sent: false,
        sms_sent: false,
        guest_count: 1,
        order_source: 'pos_web',
        items: activeCartItems.map((item, index) => ({
          product_id: item.product_id,
          product_code: item.product?.code ?? '',
          product_name: item.productName,
          line_number: index + 1,
          quantity: item.quantity,
          unit_price: item.unit_price,
          original_price: item.originalPrice,
          discount_percent: item.discount_percent,
          discount_amount: item.discount_amount,
          tax_rate_id: undefined,
          tax_percent: item.tax_rate,
          tax_amount: item.tax_amount,
          total_amount: item.total,
          cost_price: item.product?.cost_price,
          round_number: item.round_number,
          kitchen_status: 'pending',
        })),
        payments: [{
          payment_type_id: selectedPaymentType.id,
          amount: totals.total,
          tendered_amount: paymentAmountNum,
          change_amount: changeAmount,
          reference_number: `POS-${Date.now()}`
        }]
      }

      if (selectedCustomer) {
        orderData.customer_id = selectedCustomer.id
      }

      if (currentBusinessDayId) {
        orderData.business_day_id = currentBusinessDayId
      }

      const response = await apiClient.post('/api/orders', orderData)

      if (response.data && response.data.id) {
        toast.success(`Payment processed! Order #${response.data.order_number}`)
        clearCart()
        setShowPaymentDialog(false)
        setPaymentAmount('')
        setShowNewSaleDialog(true)
      }
    } catch (error: any) {
      console.error('Payment error:', error)
      const errorMessage = error.response?.data?.error || error.message || 'Payment failed'
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLockTerminal = () => {
    navigate('/lock')
  }

  const handleEndOfDay = () => {
    setShowFlyout(false)
    navigate('/end-of-day')
  }

  const handleCashInOut = () => {
    setShowFlyout(false)
    navigate('/cash-in-out')
  }

  const handleCreditPayments = () => {
    setShowFlyout(false)
    navigate('/credit-payments')
  }

  const handleSettings = () => {
    setShowFlyout(false)
    navigate('/settings')
  }

  const handleUserInfo = () => {
    setShowFlyout(false)
    navigate('/user-info')
  }

  const handleFeedback = () => {
    setShowFlyout(false)
    navigate('/feedback')
  }

  const handleMaximize = () => {
    setIsFullscreen(!isFullscreen)
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      }
    }
  }

  const handleSignOut = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
      toast.error('Failed to sign out')
    }
  }

  const handleVoidOrder = () => {
    if (cartItems.length === 0) {
      toast.error('No order to void')
      return
    }
    clearCart()
    toast.success('Order voided')
  }

  return (
    <div 
      className="h-screen flex bg-cover bg-center bg-no-repeat bg-fixed relative text-white overflow-hidden"
      style={bgLoaded ? { backgroundImage: 'url(/auth-bg.png)', backgroundSize: 'cover', backgroundAttachment: 'fixed' } : { background: '#1a1a1a' }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-gray-900/75 to-gray-900/80 pointer-events-none" />

      <div className="relative z-10 flex-1 flex flex-col overflow-hidden">
        <div className="bg-[#2a2a2a] border-b border-[#3a3a3a] px-3 py-2 flex items-center gap-3 flex-shrink-0">
          <button 
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded transition-colors ${viewMode === 'grid' ? 'bg-[#3a3a3a]' : 'hover:bg-[#333]'}`}
            title="Grid view"
          >
            <Grid size={18} />
          </button>
          <button 
            onClick={() => setViewMode('list')}
            className={`p-2 rounded transition-colors ${viewMode === 'list' ? 'bg-[#3a3a3a]' : 'hover:bg-[#333]'}`}
            title="List view"
          >
            <List size={18} />
          </button>
          <button 
            onClick={() => setViewMode('barcode')}
            className={`p-2 rounded transition-colors ${viewMode === 'barcode' ? 'bg-[#3a3a3a]' : 'hover:bg-[#333]'}`}
            title="Barcode view"
          >
            <Barcode size={18} />
          </button>

          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search products by name, code, or barcode"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchProduct(searchQuery)}
              className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:border-[#4a4a4a]"
            />
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden min-h-0">
          <div className="flex-1 flex flex-col bg-[#1a1a1a]/90 backdrop-blur-sm min-w-0">
            <div className="bg-[#2a2a2a] border-b border-[#3a3a3a] px-4 py-2.5 grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 text-xs font-semibold flex-shrink-0">
              <div>Product name</div>
              <div className="text-center">Quantity</div>
              <div className="text-right">Price</div>
              <div className="text-right">Amount</div>
            </div>

            <div className="flex-1 overflow-auto">
              {activeCartItems.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center p-8">
                  <div className="text-center space-y-4">
                    <ShoppingCart size={64} className="mx-auto text-gray-600" />
                    <div>
                      <div className="text-lg font-semibold text-gray-400 mb-1">No items in cart</div>
                      <div className="text-sm text-gray-500 max-w-md">
                        Add products by searching or selecting from categories
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full px-4 py-2">
                  {activeCartItems.map((item) => (
                    <div key={item.id} className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 py-3 border-b border-[#2a2a2a] hover:bg-[#2a2a2a]/50 group">
                      <div>
                        <div className="text-sm font-medium">{item.productName}</div>
                        <div className="text-xs text-gray-500">{item.product?.code ?? ''}</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => updateCartItemQuantity(item.id, -1)}
                            className="w-6 h-6 bg-gray-700 rounded text-xs hover:bg-gray-600 transition-colors"
                          >
                            -
                          </button>
                          <span className="text-sm font-medium min-w-[40px]">{item.quantity}</span>
                          <button
                            onClick={() => updateCartItemQuantity(item.id, 1)}
                            className="w-6 h-6 bg-gray-700 rounded text-xs hover:bg-gray-600 transition-colors"
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <div className="text-right text-sm">{item.unit_price.toFixed(2)}</div>
                      <div className="text-right">
                        <div className="text-sm font-medium">{item.total.toFixed(2)}</div>
                        {item.discount_amount > 0 && (
                          <div className="text-xs text-green-500">-{item.discount_amount.toFixed(2)}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-[#1a1a1a] border-t border-[#3a3a3a] px-4 py-4 flex-shrink-0 space-y-2">
              <div className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-2 text-sm">
                <div></div>
                <div className="text-center text-gray-400">Subtotal</div>
                <div></div>
                <div className="text-right">{totals.subtotal.toFixed(2)}</div>
              </div>
              {totals.discount > 0 && (
                <div className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-2 text-sm">
                  <div></div>
                  <div className="text-center text-gray-400">Discount</div>
                  <div></div>
                  <div className="text-right text-red-500">-{totals.discount.toFixed(2)}</div>
                </div>
              )}
              <div className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-2 text-sm">
                <div></div>
                <div className="text-center text-gray-400">Tax</div>
                <div></div>
                <div className="text-right">{totals.tax.toFixed(2)}</div>
              </div>
              <div className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-2 text-xl font-bold border-t border-[#3a3a3a] pt-2">
                <div></div>
                <div className="text-center">Total</div>
                <div></div>
                <div className="text-right">{totals.total.toFixed(2)}</div>
              </div>

              {selectedCustomer && (
                <div className="mt-2 pt-2 border-t border-[#2a2a2a]">
                  <div className="text-xs text-gray-500">Customer:</div>
                  <div className="text-sm font-medium">{selectedCustomer.company_name || `${selectedCustomer.first_name} ${selectedCustomer.last_name}`}</div>
                </div>
              )}
            </div>
          </div>

          <div className="relative flex flex-shrink-0">
            <div
              className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-blue-500 transition-colors z-10"
              onMouseDown={handleMouseDown}
              style={{ marginLeft: '-2px' }}
            />

            <div 
              className="bg-[#252525]/90 backdrop-blur-sm border-l border-[#3a3a3a] p-2.5 flex flex-col overflow-y-auto"
              style={{ width: `${rightPanelWidth}px` }}
            >
              <div className="flex flex-col flex-grow">
                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-4 gap-2">
                    <button 
                      onClick={() => setShowSearchDialog(true)}
                      className="bg-[#2a2a2a] hover:bg-[#333] p-2.5 rounded flex flex-col items-center justify-center gap-0.5 border border-[#3a3a3a] transition-colors min-h-[65px]"
                    >
                      <Search size={18} />
                      <span className="text-[10px] mt-0.5">Search</span>
                      <span className="text-[9px] text-gray-500">F3</span>
                    </button>
                    <button 
                      onClick={() => setShowQuantityDialog(true)}
                      className="bg-[#2a2a2a] hover:bg-[#333] p-2.5 rounded flex flex-col items-center justify-center gap-0.5 border border-[#3a3a3a] transition-colors min-h-[65px]"
                    >
                      <ShoppingCart size={18} />
                      <span className="text-[10px] mt-0.5">Quantity</span>
                      <span className="text-[9px] text-gray-500">F4</span>
                    </button>
                    <button 
                      onClick={() => setShowCustomerDialog(true)}
                      className="bg-[#2a2a2a] hover:bg-[#333] p-2.5 rounded flex flex-col items-center justify-center gap-0.5 border border-[#3a3a3a] transition-colors min-h-[65px]"
                    >
                      <Users size={18} />
                      <span className="text-[10px] mt-0.5">Customer</span>
                    </button>
                    <button 
                      onClick={() => setShowNewSaleDialog(true)}
                      className="bg-[#2a2a2a] hover:bg-[#333] p-2.5 rounded flex flex-col items-center justify-center gap-0.5 border border-[#3a3a3a] transition-colors min-h-[65px]"
                    >
                      <Plus size={18} />
                      <span className="text-[10px] mt-0.5">New sale</span>
                      <span className="text-[9px] text-gray-500">F8</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {paymentTypes.slice(0, 3).map((paymentType) => (
                      <button
                        key={paymentType.id}
                        onClick={() => {
                          setSelectedPaymentType(paymentType)
                          if (paymentType.code?.toLowerCase() === 'cash') {
                            handleCashPayment()
                          } else {
                            handlePayment()
                          }
                        }}
                        className={`p-2.5 rounded flex flex-col items-center justify-center gap-0.5 border transition-colors min-h-[55px] ${
                          selectedPaymentType?.id === paymentType.id
                            ? 'bg-blue-600 border-blue-500'
                            : 'bg-[#2a2a2a] hover:bg-[#333] border-[#3a3a3a]'
                        }`}
                      >
                        {paymentType.code?.toLowerCase() === 'cash' ? (
                          <DollarSign size={18} />
                        ) : paymentType.code?.toLowerCase() === 'card' ? (
                          <CreditCard size={18} />
                        ) : (
                          <FileText size={18} />
                        )}
                        <span className="text-[10px] mt-0.5">{paymentType.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex-1" />

                <button className="bg-[#2a2a2a] hover:bg-[#333] p-3 rounded flex flex-col items-center justify-center gap-1.5 border border-[#3a3a3a] transition-colors mb-2">
                  <Package size={22} />
                  <span className="text-xs">Cash drawer</span>
                </button>

                <div className="grid grid-cols-4 gap-2 mb-2">
                  <button 
                    onClick={() => setShowDiscountDialog(true)}
                    className="bg-[#2a2a2a] hover:bg-[#333] p-2.5 rounded flex flex-col items-center justify-center gap-0.5 border border-[#3a3a3a] transition-colors min-h-[60px]"
                  >
                    <Percent size={18} />
                    <span className="text-[10px] mt-0.5">Discount</span>
                    <span className="text-[9px] text-gray-500">F2</span>
                  </button>
                  <button 
                    onClick={() => setShowCommentDialog(true)}
                    className="bg-[#2a2a2a] hover:bg-[#333] p-2.5 rounded flex flex-col items-center justify-center gap-0.5 border border-[#3a3a3a] transition-colors min-h-[60px]"
                  >
                    <MessageCircle size={18} />
                    <span className="text-[10px] mt-0.5">Comment</span>
                  </button>
                  <button 
                    onClick={handleSaveSale}
                    className="bg-[#2a2a2a] hover:bg-[#333] p-2.5 rounded flex flex-col items-center justify-center gap-0.5 border border-[#3a3a3a] transition-colors min-h-[60px]"
                  >
                    <Save size={18} />
                    <span className="text-[10px] mt-0.5">Save</span>
                    <span className="text-[9px] text-gray-500">F9</span>
                  </button>
                  <button className="bg-[#2a2a2a] hover:bg-[#333] p-2.5 rounded flex flex-col items-center justify-center gap-0.5 border border-[#3a3a3a] transition-colors min-h-[60px]">
                    <span className="text-base">---</span>
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-2">
                  <button 
                    onClick={handleSaveSale}
                    className="bg-[#2a2a2a] hover:bg-[#333] p-3 rounded flex flex-col items-center justify-center gap-0.5 border border-[#3a3a3a] transition-colors"
                  >
                    <span className="text-[10px] text-gray-400">F9</span>
                    <span className="text-xs font-semibold mt-0.5">Save sale</span>
                  </button>
                  <button className="bg-[#2a2a2a] hover:bg-[#333] p-3 rounded flex flex-col items-center justify-center gap-0.5 transition-colors">
                    <RotateCcw size={18} />
                    <span className="text-xs font-semibold mt-0.5">Refund</span>
                  </button>
                </div>

                <button 
                  onClick={handlePayment}
                  className="bg-green-600 hover:bg-green-700 p-3 rounded flex flex-col items-center justify-center gap-0.5 text-lg font-bold transition-colors mb-2"
                >
                  <span className="text-sm">F10</span>
                  <span className="text-lg font-semibold">Payment</span>
                </button>

                <div className="grid grid-cols-4 gap-2">
                  <button 
                    onClick={handleLockTerminal}
                    className="bg-[#2a2a2a] hover:bg-[#333] p-2.5 rounded flex flex-col items-center justify-center gap-0.5 border border-[#3a3a3a] transition-colors min-h-[60px]"
                  >
                    <Lock size={18} />
                    <span className="text-[10px] mt-0.5">Lock</span>
                    <span className="text-[9px] text-gray-500">F7</span>
                  </button>
                  <button className="bg-[#2a2a2a] hover:bg-[#333] p-2.5 rounded flex flex-col items-center justify-center gap-0.5 border border-[#3a3a3a] transition-colors min-h-[60px]">
                    <Plus size={18} />
                    <span className="text-[10px] mt-0.5">Transfer</span>
                    <span className="text-[9px] text-gray-500">F7</span>
                  </button>
                  <button 
                    onClick={handleVoidOrder}
                    className="bg-red-600 hover:bg-red-700 p-2.5 rounded flex flex-col items-center justify-center gap-0.5 transition-colors min-h-[60px]"
                  >
                    <Trash2 size={18} />
                    <span className="text-[12px] mt-0.5">Void order</span>
                  </button>
                  <button 
                    onClick={() => setShowFlyout(true)}
                    className="bg-[#2a2a2a] hover:bg-[#333] p-2.5 mt-0.5 rounded flex flex-col items-center justify-center gap-0.5 border border-[#3a3a3a] transition-colors min-h-[60px]"
                  >
                    <Menu size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showFlyout && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-60 z-40"
            onClick={() => setShowFlyout(false)}
          />
          <div className="fixed top-0 right-0 bottom-0 w-80 bg-[#2a2a2a]/95 backdrop-blur-md shadow-2xl z-50 flex flex-col animate-slide-in-right border-l border-[#3a3a3a]">
            <div className="bg-[#1a1a1a]/90 backdrop-blur-sm p-4 border-b border-[#3a3a3a] flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold">POS Terminal</h2>
                <p className="text-xs text-gray-400 mt-0.5">{(user as any)?.first_name} {(user as any)?.last_name}</p>
              </div>
              <button onClick={() => setShowFlyout(false)}>
                <ChevronRight size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <button 
                onClick={handleCashInOut}
                className="w-full px-5 py-3.5 hover:bg-[#333] flex items-center gap-3 border-b border-[#3a3a3a] transition-colors text-sm"
              >
                <DollarSign size={19} />
                <span>Cash In / Out</span>
              </button>

              <button 
                onClick={handleCreditPayments}
                className="w-full px-5 py-3.5 hover:bg-[#333] flex items-center gap-3 border-b border-[#3a3a3a] transition-colors text-sm"
              >
                <CreditCard size={19} />
                <span>Credit payments</span>
              </button>

              <button
                onClick={handleEndOfDay}
                className="w-full px-5 py-3.5 hover:bg-[#333] flex items-center gap-3 border-b border-[#3a3a3a] transition-colors text-sm"
              >
                <Power size={19} />
                <span>End of day</span>
              </button>

              <div className="border-t border-[#3a3a3a] mt-3 pt-3">
                <div className="px-5 py-1.5 text-xs text-gray-500">User</div>

                <button 
                  onClick={handleUserInfo}
                  className="w-full px-5 py-3.5 hover:bg-[#333] flex items-center gap-3 border-b border-[#3a3a3a] transition-colors text-sm"
                >
                  <UserCircle size={19} />
                  <span>User info</span>
                </button>

                <button 
                  onClick={handleSignOut}
                  className="w-full px-5 py-3.5 hover:bg-[#333] flex items-center gap-3 transition-colors text-sm"
                >
                  <LogOut size={19} />
                  <span>Sign out</span>
                </button>
              </div>

              <button 
                onClick={handleFeedback}
                className="w-full px-5 py-3.5 hover:bg-[#333] flex items-center gap-3 border-t border-[#3a3a3a] mt-3 transition-colors text-sm"
              >
                <MessageSquare size={19} />
                <span>Feedback</span>
              </button>
            </div>

            <div className="p-3.5 border-t border-[#3a3a3a] flex items-center justify-between">
              <span className="text-xs text-gray-400">
                {new Date().toLocaleDateString('en-GB')}
              </span>
              <div className="flex items-center gap-2.5">
                <button 
                  onClick={handleSettings}
                  className="p-1.5 hover:bg-[#333] rounded transition-colors"
                  title="Settings"
                >
                  <ShieldCheck size={18} />
                </button>
                <button 
                  onClick={handleMaximize}
                  className="p-1.5 hover:bg-[#333] rounded transition-colors"
                  title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                >
                  <Maximize size={18} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Search Dialog */}
      {showSearchDialog && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm"
          onClick={(e) => handleDialogOverlayClick(e, 'search')}
        >
          <div className="bg-[#2a2a2a]/95 backdrop-blur-md rounded-lg p-6 w-[600px] border border-[#3a3a3a] max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Search Products</h3>
              <button onClick={() => setShowSearchDialog(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-3 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Product name"
                  className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#4a4a4a]"
                />
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Product code"
                  className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#4a4a4a]"
                />
              </div>
              <div className="relative">
                <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Barcode"
                  className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#4a4a4a]"
                />
              </div>
            </div>

            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {products.slice(0, 10).map((product) => (
                <button
                  key={product.id}
                  onClick={() => {
                    addProductToCart(product)
                    setShowSearchDialog(false)
                  }}
                  className="w-full p-3 bg-[#1a1a1a] hover:bg-[#2a2a2a] rounded border border-[#3a3a3a] text-left transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">{product.name}</div>
                      <div className="text-sm text-gray-400 mt-1">
                        Code: {product.code} | Stock: {product.current_stock || 0}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">${product.price.toFixed(2)}</div>
                      <div className="text-xs text-gray-500">{product.category_name}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-6 p-4 border-t border-[#3a3a3a] flex justify-end gap-2.5">
              <button 
                onClick={() => setShowSearchDialog(false)}
                className="px-5 py-2 bg-red-600 hover:bg-red-700 rounded text-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quantity Dialog */}
      {showQuantityDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={(e) => handleDialogOverlayClick(e, 'quantity')}>
          <div className="bg-[#2a2a2a]/95 backdrop-blur-md rounded-lg w-96 border border-[#3a3a3a]">
            <div className="p-4 border-b border-[#3a3a3a]">
              <h2 className="text-lg font-semibold">Change quantity</h2>
              <p className="text-xs text-gray-400 mt-0.5">Enter quantity for next item</p>
            </div>

            <div className="p-5">
              <div className="bg-[#1a1a1a] border-2 border-blue-500 rounded p-3 mb-3 text-right text-2xl font-bold">
                {quantityInput}
              </div>

              <div className="grid grid-cols-3 gap-2">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '-', '0', '.'].map((key) => (
                  <button
                    key={key}
                    onClick={() => handleNumericKey(key)}
                    className="bg-[#3a3a3a] hover:bg-[#444] p-3 rounded text-lg font-semibold transition-colors"
                  >
                    {key}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-2 mt-2">
                <button
                  onClick={() => handleNumericKey('ESC')}
                  className="bg-red-600 hover:bg-red-700 p-2.5 rounded text-xs font-semibold transition-colors"
                >
                  ESC
                </button>
                <button
                  onClick={() => handleNumericKey('←')}
                  className="bg-orange-600 hover:bg-orange-700 p-2.5 rounded text-xs font-semibold transition-colors"
                >
                  ←
                </button>
                <button
                  onClick={() => handleNumericKey('Enter')}
                  className="bg-green-600 hover:bg-green-700 p-2.5 rounded text-xs font-semibold transition-colors"
                >
                  ↵ Enter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Sale Dialog */}
      {showNewSaleDialog && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm"
          onClick={(e) => handleDialogOverlayClick(e, 'newSale')}
        >
          <div className="bg-[#2a2a2a]/95 backdrop-blur-md rounded-lg p-6 w-[500px] border border-[#3a3a3a]">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Start New Sale</h3>
              <button onClick={() => setShowNewSaleDialog(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="flex flex-col items-center mb-8">
              <div className="text-6xl mb-4">🛒</div>
              <input
                ref={newSaleInputRef}
                type="text"
                value={newSaleInput}
                onChange={(e) => setNewSaleInput(e.target.value)}
                placeholder="Enter customer name or order reference"
                className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded px-4 py-3 text-center focus:outline-none focus:border-[#4a4a4a]"
                autoFocus
              />
              <p className="text-sm text-gray-400 mt-2">
                This will clear the current cart and start a new sale
              </p>
            </div>

            <div className="flex justify-center gap-3">
              <button 
                onClick={() => {
                  clearCart()
                  setShowNewSaleDialog(false)
                  toast.success('New sale started')
                }}
                className="bg-green-600 hover:bg-green-700 px-8 py-3 rounded font-semibold"
              >
                Start New Sale
              </button>
              <button 
                onClick={() => setShowNewSaleDialog(false)}
                className="bg-red-600 hover:bg-red-700 px-8 py-3 rounded font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Dialog */}
      {showPaymentDialog && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm"
          onClick={(e) => handleDialogOverlayClick(e, 'payment')}
        >
          <div className="bg-[#2a2a2a]/95 backdrop-blur-md rounded-lg p-6 w-[500px] border border-[#3a3a3a]">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Process Payment</h3>
              <button onClick={() => setShowPaymentDialog(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-[#1a1a1a] p-4 rounded border border-[#3a3a3a]">
                <div className="text-sm text-gray-400 mb-1">Total Amount</div>
                <div className="text-3xl font-bold text-right">{totals.total.toFixed(2)}</div>
              </div>

              <div>
                <div className="text-sm text-gray-400 mb-2">Payment Type</div>
                <div className="grid grid-cols-3 gap-2">
                  {paymentTypes.map((paymentType) => (
                    <button
                      key={paymentType.id}
                      onClick={() => setSelectedPaymentType(paymentType)}
                      className={`p-3 rounded border transition-colors ${
                        selectedPaymentType?.id === paymentType.id
                          ? 'bg-blue-600 border-blue-500'
                          : 'bg-[#1a1a1a] hover:bg-[#2a2a2a] border-[#3a3a3a]'
                      }`}
                    >
                      <div className="font-medium">{paymentType.name}</div>
                      <div className="text-xs text-gray-400 mt-1">{paymentType.code}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-400 mb-2">Tendered Amount</div>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded px-4 py-3 text-2xl text-right focus:outline-none focus:border-[#4a4a4a]"
                  autoFocus
                />
              </div>

              {paymentAmount && parseFloat(paymentAmount) > 0 && (
                <div className="bg-[#1a1a1a] p-4 rounded border border-[#3a3a3a]">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-400">Change Due</div>
                    <div className="text-2xl font-bold text-green-500">
                      {Math.max(0, parseFloat(paymentAmount) - totals.total).toFixed(2)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <button 
                onClick={processPayment}
                disabled={!selectedPaymentType || !paymentAmount || parseFloat(paymentAmount) < totals.total}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed px-8 py-3 rounded font-semibold"
              >
                Process Payment
              </button>
              <button 
                onClick={() => setShowPaymentDialog(false)}
                className="bg-red-600 hover:bg-red-700 px-8 py-3 rounded font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}